import { useState, useMemo } from "react";
import { PARTNERS, scoreRAG, kpiRAG, COMPLIANCE_META, TONE_CHIP } from "../../data/ybf/partners";
import { PILLARS, getPillar } from "../../data/ybf/pillars";
import { fmtCr, fmtInt } from "../../data/ybf/utils";
import { Search, Handshake, Star, Target, FileText, AlertTriangle, CheckCircle2 } from "lucide-react";

const COMPLIANCE_ICON = {
  all_submitted:   CheckCircle2,
  reports_overdue: FileText,
  uc_overdue:      AlertTriangle,
};

export default function Partners() {
  const [query, setQuery] = useState("");
  const [pillarFilter, setPillarFilter] = useState("all");

  const filtered = useMemo(() => {
    return PARTNERS.filter((p) => {
      if (pillarFilter !== "all" && p.pillar !== pillarFilter) return false;
      if (query && !p.name.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [query, pillarFilter]);

  return (
    <div data-testid="ybf-partners" className="space-y-5 pb-8">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Handshake size={18} className="text-[#00204E]" />
            <h1 className="text-2xl font-bold text-slate-900">Implementation Partners</h1>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {PARTNERS.length} partners delivering programmes on the ground across all 3 pillars
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total Sanctioned</div>
          <div className="text-xl font-bold text-[#00204E]">
            {fmtCr(PARTNERS.reduce((s, p) => s + p.fundedCr, 0))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search partners..."
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00204E]/20 focus:border-[#00204E]"
            data-testid="partner-search-input"
          />
        </div>
        <div className="flex gap-1 bg-white p-1 rounded-lg border border-slate-200">
          <FilterBtn active={pillarFilter === "all"}            onClick={() => setPillarFilter("all")}            label="All" testid="filter-all" />
          {PILLARS.map((p) => (
            <FilterBtn
              key={p.id}
              active={pillarFilter === p.id}
              onClick={() => setPillarFilter(p.id)}
              label={p.name.split(" ")[0]}
              accent={p.color}
              testid={`filter-${p.id}`}
            />
          ))}
        </div>
      </div>

      {/* Partner cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((pt) => {
          const p = getPillar(pt.pillar);
          return (
            <div
              key={pt.id}
              data-testid={`partner-card-${pt.id}`}
              className="bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md transition-shadow relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-1 h-full" style={{ background: p?.color }} />
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 pr-2">
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${p?.chipBg} ${p?.chipText}`}>
                    {p?.short} · {p?.name.split(" ")[0]}
                  </span>
                  <h3 className="text-sm font-bold text-slate-900 mt-1.5 leading-snug">{pt.name}</h3>
                </div>
              </div>
              <div className="text-[10px] text-slate-500 mt-2">
                <span className="font-semibold text-slate-700">States:</span> {pt.states.join(", ")}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-3">
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">
                    {pt.trees ? "Trees" : pt.msmes ? "MSMEs" : "Beneficiaries"}
                  </div>
                  <div className="text-sm font-bold text-slate-900">
                    {pt.trees ? fmtInt(pt.trees) : pt.msmes ? fmtInt(pt.msmes) : fmtInt(pt.beneficiaries)}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">Sanctioned</div>
                  <div className="text-sm font-bold text-slate-900">{fmtCr(pt.fundedCr)}</div>
                </div>
                <div
                  className="bg-slate-50 rounded-lg p-2"
                  data-testid={`partner-utilised-${pt.id}`}
                >
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">Utilised</div>
                  <div className="text-sm font-bold text-slate-900 leading-tight">
                    {fmtCr(pt.utilisedCr)}
                  </div>
                  <div
                    className={`inline-block mt-0.5 px-1 rounded text-[9px] font-bold ${
                      pt.utilisationPct >= 85
                        ? "text-emerald-700 bg-emerald-50"
                        : pt.utilisationPct >= 70
                        ? "text-amber-700 bg-amber-50"
                        : "text-rose-700 bg-rose-50"
                    }`}
                  >
                    {pt.utilisationPct}%
                  </div>
                </div>
                <div
                  className="bg-slate-50 rounded-lg p-2"
                  data-testid={`partner-grant-count-${pt.id}`}
                >
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">
                    # of Grants
                  </div>
                  <div className="text-sm font-bold text-slate-900">{pt.grantCount}</div>
                </div>
              </div>

              {/* Performance + Compliance chips */}
              {(() => {
                const score = scoreRAG(pt.avgScore);
                const kpi   = kpiRAG(pt.kpiCompletion);
                const comp  = COMPLIANCE_META[pt.compliance.type];
                const CompIcon = COMPLIANCE_ICON[pt.compliance.type];
                const compLabel = pt.compliance.type === "all_submitted"
                  ? comp.label
                  : `${comp.label} · ${pt.compliance.count} grant${pt.compliance.count > 1 ? "s" : ""}`;
                return (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`partner-chips-${pt.id}`}>
                    <span
                      data-testid={`partner-score-${pt.id}`}
                      title={`Average Project Score · ${score.label}`}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${TONE_CHIP[score.tone]}`}
                    >
                      <Star size={9} fill={score.dot} strokeWidth={0} />
                      {pt.avgScore.toFixed(2)}/5
                    </span>
                    <span
                      data-testid={`partner-kpi-${pt.id}`}
                      title={`Average KPI Completion · ${kpi.label}`}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${TONE_CHIP[kpi.tone]}`}
                    >
                      <Target size={9} strokeWidth={2.5} />
                      {pt.kpiCompletion}% KPIs
                    </span>
                    <span
                      data-testid={`partner-compliance-${pt.id}`}
                      title={compLabel}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${TONE_CHIP[comp.tone]}`}
                    >
                      <CompIcon size={9} strokeWidth={2.5} />
                      {compLabel}
                    </span>
                  </div>
                );
              })()}

              <div className="mt-3 text-[10px] text-slate-500">Partnering since <span className="font-semibold text-slate-700">{pt.since}</span></div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">
          No partners match your filters.
        </div>
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, label, accent, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all ${
        active ? "text-white shadow" : "text-slate-600 hover:bg-slate-50"
      }`}
      style={active ? { background: accent || "#00204E" } : {}}
    >
      {label}
    </button>
  );
}
